/**
 * Ollama AI Service
 * Handles communication with local Ollama server for AI-assisted form filling
 */

// Configuration from environment variables
const OLLAMA_API_URL = import.meta.env.VITE_OLLAMA_API_URL || 'http://localhost:11434';
const OLLAMA_MODEL = import.meta.env.VITE_OLLAMA_MODEL || 'llama3.1:8b';

/**
 * Ollama API response structure
 */
interface OllamaResponse {
  model: string;
  created_at: string;
  response: string;
  done: boolean;
  total_duration?: number;
  load_duration?: number;
  prompt_eval_count?: number;
  eval_count?: number;
}

/**
 * Field types for generation
 */
export type FieldType = 'short' | 'long' | 'list';

/**
 * Context passed to AI for generation
 */
export interface GenerationContext {
  category?: string;
  qualityTier?: string;
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  keywords?: string;
  priceAmount?: number;
  priceCurrency?: string;
}

/**
 * Prompt templates for different fields
 */
const PROMPT_TEMPLATES: Record<string, (ctx: GenerationContext) => string> = {
  title: (ctx) => `Generate a compelling product title for a ${ctx.qualityTier || 'basic'} quality ${formatCategory(ctx.category || 'betting')} strategy guide.

Requirements:
- Keep it under 60 characters
- Make it engaging and professional
- Do NOT use quotes around the title
- Focus on the value proposition
- Suitable for a marketplace listing

Respond with ONLY the title, nothing else.`,

  shortDescription: (ctx) => `Write a concise 1-2 sentence description for a betting strategy product.

Product Title: ${ctx.title || 'Strategy Guide'}
Category: ${formatCategory(ctx.category || 'betting')}
Quality Tier: ${ctx.qualityTier || 'basic'}
${ctx.keywords ? `Keywords: ${ctx.keywords}` : ''}

Requirements:
- Maximum 200 characters
- Highlight the main benefit
- Be persuasive but honest
- Include a call-to-action feel

Respond with ONLY the description, nothing else.`,

  longDescription: (ctx) => `Write a detailed product description for a betting/gambling strategy guide.

Product Title: ${ctx.title || 'Strategy Guide'}
Short Description: ${ctx.shortDescription || 'A comprehensive strategy guide'}
Category: ${formatCategory(ctx.category || 'betting')}
Quality Tier: ${ctx.qualityTier || 'basic'}
${ctx.keywords ? `Keywords: ${ctx.keywords}` : ''}

Requirements:
- 2-4 paragraphs
- Explain what the buyer will learn
- Highlight key features and benefits
- Include a disclaimer that this is for educational purposes only
- Professional but engaging tone
- Do NOT make specific profit/win guarantees

Respond with ONLY the description, nothing else.`,

  keywords: (ctx) => `Suggest 5-8 SEO keywords/phrases for this betting strategy product.

Product Title: ${ctx.title || 'Strategy Guide'}
Category: ${formatCategory(ctx.category || 'betting')}
${ctx.shortDescription ? `Description: ${ctx.shortDescription}` : ''}

Requirements:
- Comma-separated list
- Mix of single words and 2-3 word phrases
- Include category-specific terms
- Include general betting/strategy terms
- No duplicates

Respond with ONLY the comma-separated keywords, nothing else.`,
};

/**
 * Format category slug to readable text
 */
function formatCategory(category: string): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Check if Ollama server is available
 */
export async function checkOllamaConnection(): Promise<boolean> {
  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/tags`, {
      method: 'GET',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get available models from Ollama
 */
export async function getAvailableModels(): Promise<string[]> {
  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/tags`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch models');
    }

    const data = await response.json();
    return data.models?.map((m: { name: string }) => m.name) || [];
  } catch (error) {
    console.error('Failed to get Ollama models:', error);
    return [];
  }
}

/**
 * Generate content for a specific form field using AI
 *
 * @param fieldName - The name of the field to generate content for
 * @param context - Current form values for context
 * @returns Generated text for the field
 */
export async function generateFieldSuggestion(
  fieldName: keyof typeof PROMPT_TEMPLATES,
  context: GenerationContext
): Promise<string> {
  const promptTemplate = PROMPT_TEMPLATES[fieldName];

  if (!promptTemplate) {
    throw new Error(`No prompt template for field: ${fieldName}`);
  }

  const prompt = promptTemplate(context);

  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: OLLAMA_MODEL,
        prompt,
        stream: false,
        options: {
          temperature: 0.7,
          top_p: 0.9,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ollama API error: ${response.status} - ${errorText}`);
    }

    const data: OllamaResponse = await response.json();

    // Clean up the response
    let result = data.response.trim();

    // Remove surrounding quotes if present
    if ((result.startsWith('"') && result.endsWith('"')) ||
        (result.startsWith("'") && result.endsWith("'"))) {
      result = result.slice(1, -1);
    }

    return result;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Cannot connect to Ollama server. Is it running?');
    }
    throw error;
  }
}

/**
 * Ollama service object for convenience
 */
export const ollamaService = {
  checkConnection: checkOllamaConnection,
  getModels: getAvailableModels,
  generateField: generateFieldSuggestion,
};
